<template>
   <section class="modern-login-content">
      <div class="login-wrapper">
         <!-- Left Visual Section -->
         <div class="visual-section">
            <div class="visual-background">
               <!-- Animated Gradient Mesh & SVG Elements -->
               <svg class="bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                     <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#16213e;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#1a1a2e;stop-opacity:1" />
                     </linearGradient>
                  </defs>
                  
                  <!-- Floating Particles -->
                  <circle cx="20" cy="80" r="1.5" fill="#3a7bd5" class="particle p1" />
                  <circle cx="80" cy="20" r="1" fill="#00d2ff" class="particle p2" />
                  <circle cx="50" cy="50" r="2" fill="#3a7bd5" class="particle p3" />
                  <circle cx="70" cy="80" r="1.2" fill="#00d2ff" class="particle p4" />
                  <circle cx="10" cy="30" r="0.8" fill="#3a7bd5" class="particle p5" />
                  
                  <!-- Floating Chart Elements -->
                  <g class="floating-chart c1">
                     <rect x="25" y="60" width="4" height="15" rx="1" fill="rgba(0, 210, 255, 0.4)" />
                     <rect x="32" y="50" width="4" height="25" rx="1" fill="rgba(58, 123, 213, 0.4)" />
                     <rect x="39" y="40" width="4" height="35" rx="1" fill="rgba(0, 210, 255, 0.6)" />
                     <rect x="46" y="30" width="4" height="45" rx="1" fill="rgba(58, 123, 213, 0.6)" />
                  </g>
                  
                  <!-- Abstract Shapes -->
                  <g class="floating-cube c2">
                     <polygon points="70,40 75,37 80,40 75,43" fill="#3a7bd5" opacity="0.5"/>
                     <polygon points="70,40 75,43 75,49 70,46" fill="#16213e" opacity="0.4"/>
                     <polygon points="75,43 80,40 80,46 75,49" fill="#0f3460" opacity="0.6"/>
                  </g>
                  
                  <g class="floating-cube c3">
                     <polygon points="20,20 23,18 26,20 23,22" fill="#00d2ff" opacity="0.4"/>
                     <polygon points="20,20 23,22 23,26 20,24" fill="#3a7bd5" opacity="0.3"/>
                     <polygon points="23,22 26,20 26,24 23,26" fill="#0f3460" opacity="0.5"/>
                  </g>
               </svg>
            </div>
            
            <div class="brand-content">
               <h1 class="brand-title">COUPLUS</h1>
               <p class="brand-subtitle">쿠팡 셀러를 위한 올인원 AI 솔루션</p>
               
               <div class="feature-list">
                  <div class="feature-item">
                     <div class="icon-box"><i class="ri-robot-line"></i></div>
                     <span>AI 상품등록</span>
                  </div>
                  <div class="feature-item">
                     <div class="icon-box"><i class="ri-bar-chart-line"></i></div>
                     <span>실시간 분석</span>
                  </div>
                  <div class="feature-item">
                     <div class="icon-box"><i class="ri-shield-check-line"></i></div>
                     <span>스마트 관리</span>
                  </div>
               </div>
            </div>
         </div>

         <!-- Right Form Section -->
         <div class="form-section">
            <div class="glass-panel">
               <div class="form-header">
                  <h2>로그인</h2>
                  <p>계정에 로그인하여 서비스를 이용하세요</p>
               </div>
               
               <!-- 오류 메시지 -->
               <div v-if="errorMessage" class="error-alert">
                  <i class="ri-error-warning-line"></i> {{ errorMessage }}
               </div>

               <!-- 로그인 폼 -->
               <form @submit.prevent="onSubmit()" class="login-form">
                  <div class="input-group">
                     <i class="ri-mail-line input-icon"></i>
                     <input type="email" v-model="email" :disabled="isLoading" required placeholder="이메일 주소" class="glass-input">
                  </div>
                  
                  <div class="input-group">
                     <i class="ri-lock-line input-icon"></i>
                     <input type="password" v-model="pw" :disabled="isLoading" required placeholder="비밀번호" class="glass-input">
                  </div>
                  
                  <div class="form-actions">
                     <label class="checkbox-container">
                        <input type="checkbox" v-model="rememberMe">
                        <span class="checkmark"></span>
                        로그인 상태 유지
                     </label>
                     
                     <router-link :to="{name: 'auth.forgot-password'}" class="forgot-link">비밀번호를 잊으셨나요?</router-link>
                  </div>
                  
                  <button type="submit" class="submit-btn" :disabled="isLoading || !email || !pw">
                     <span v-if="isLoading" class="btn-content">
                        <i class="ri-loader-4-line spin-icon"></i> 로그인 중...
                     </span>
                     <span v-else class="btn-content">
                        로그인
                     </span>
                  </button>
                  
                  <div class="register-link">
                     아직 계정이 없으신가요? <router-link :to="{name: 'auth.register'}">회원가입</router-link>
                  </div>
               </form>
            </div>
         </div>
      </div>

      <!-- 이미 승인된 계정의 무료 이용권 — 로그인 성공 뒤 이용권이 있을 때만 뜬다(구독 모달의 이용권 칸과 같은 모양). -->
      <b-modal id="modal-coupon" title="무료 이용권" hide-footer no-close-on-backdrop @hide="onCouponModalHide">
         <div class="alert alert-success mb-3">
            <strong>{{ coupon.days }}일 무료 이용권이 있습니다.</strong>
            <div class="mt-1">사용하면 {{ coupon.days }}일 동안 <strong>AI 로켓등록</strong>이 열립니다. 지금 쓰시는 다른 기능은 그대로입니다.</div>
            <div class="mt-1">기간이 끝나면 이용권으로 열린 것만 닫힙니다.</div>
         </div>
         <div v-if="couponError" class="alert alert-warning mb-3">{{ couponError }}</div>
         <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary mr-2" :disabled="couponBusy" @click="skipCoupon">나중에</button>
            <button type="button" class="btn btn-success" :disabled="couponBusy" @click="useCoupon">
               {{ couponBusy ? '처리 중...' : '이용권 사용하기' }}
            </button>
         </div>
      </b-modal>

      <!-- 기존 구독 모달 (완전히 그대로 유지) -->
      <b-modal id="modal-reg" size="lg" title="쿠플러스 구독 결제" ok-only ok-title='취소'>
         <b-card>
            <!-- 무료 이용권이 있는 사람에게만 보인다. 없으면 아예 그려지지 않아 기존 화면과 같다. -->
            <div v-if="coupon.available"
               class="alert alert-success d-flex justify-content-between align-items-center mb-3">
               <div class="mr-3">
                  <strong>{{ coupon.days }}일 무료 이용권이 있습니다.</strong>
                  <div class="mt-1">결제 없이 바로 시작할 수 있습니다. 기간이 끝나면 이 화면이 다시 표시됩니다.</div>
               </div>
               <button type="button" class="btn btn-success flex-shrink-0"
                  :disabled="couponBusy" @click="useCoupon">
                  {{ couponBusy ? '처리 중...' : '무료로 시작하기' }}
               </button>
            </div>
            <div v-if="couponError" class="alert alert-warning mb-3">{{ couponError }}</div>
            <div class="terms_box" tabindex="0" id="divPrivacy" style="font-size:80%">
               <form @submit.prevent="submitForm">
                  <div class="container-fluid">
                     <b-row>
                        <b-col lg="6" sm="6">
                           <card
                              class=" card-block card-stretch card-height blog pricing-details"
                              bodyClass="border text-center rounded">
                              <template v-slot:body>
                                 <div class="pricing-header">
                                    <div class="icon-data"><i class="ri-star-line"></i>
                                    </div>
                                    <h2 class="mt-4 mb-4 display-5 font-weight-bolder">
                                       {{ planPriceText('COUPLUS-Lite') }}<small class="font-size-14 text-muted">/
                                          Month</small></h2>
                                 </div>
                                 <h3 class="mb-3">라이트</h3>
                                 <b-form-radio v-model="selected" name="some-radios"
                                    value="lite" button
                                    button-variant="outline-primary">선택</b-form-radio>
                                 <ul class="list-unstyled mt-3 mb-0">
                                    <li>로켓그로스 상품관리 (노출ID변경·매입·마진·뱃지·번들·타사매칭)</li>
                                    <li>로켓그로스 SCM (공급·재고관리·구매알림·구매수량 자동제안)</li>
                                    <li>판매장부 (상품별·날짜별 판매 관리)</li>
                                    <li>(확장) 판매량 조회 (전체판매량·조회수·전환율)</li>
                                    <li>1688 상품 탐색</li>
                                    <li>쿠팡 랭킹추적 (200슬롯)</li>
                                    <li>키워드 분석</li>
                                    <li>제트ON/신규ON/리뷰분석 기능</li>
                                 </ul>

                              </template>
                           </card>
                        </b-col>
                        <b-col lg="6" sm="6">
                           <card
                              class=" card-block card-stretch card-height blog pricing-details"
                              bodyClass="border text-center rounded">
                              <template v-slot:body>
                                 <div class="pricing-header">
                                    <div class="icon-data"><i class="ri-star-line"></i><i
                                          class="ri-star-line"></i><i class="ri-star-line"></i>
                                    </div>
                                    <h2 class="mt-4 mb-4 display-5 font-weight-bolder">
                                       {{ planPriceText('COUPLUS-Premium') }}<small class="font-size-14 text-muted">/
                                          Month</small></h2>
                                 </div>
                                 <h3 class="mb-3">프리미엄</h3>
                                 <b-form-radio v-model="selected" name="some-radios"
                                    value="premium" button
                                    button-variant="outline-primary">선택</b-form-radio>
                                 <ul class="list-unstyled mt-3 mb-0">
                                    <li>로켓그로스 상품관리 (노출ID변경·매입·마진·뱃지·번들·타사매칭)</li>
                                    <li>로켓그로스 SCM (공급·재고관리·구매알림·구매수량 자동제안)</li>
                                    <li>판매장부 (상품별·날짜별 판매 관리)</li>
                                    <li>(확장) 판매량 조회 (전체판매량·조회수·전환율 + 옵션별판매량·전환키워드·반품율)</li>
                                    <li>1688 상품 탐색</li>
                                    <li>쿠팡 랭킹추적 (2000슬롯)</li>
                                    <li>키워드 분석</li>
                                    <li>신규ON/리뷰분석 기능</li>
                                    <li class="mt-2">AI 로켓등록 (1688 상품수집·가격설정·상세페이지 자동생성)</li>
                                    <li>로켓배송 상품관리 (일자별 재고·출고현황)</li>
                                    <li>판매통계 기반 로켓 발주 필요수량 예측</li>
                                    <li>바코드 프린트 (한글표시사항 포함)</li>
                                    <li>로켓 발주 내역 및 세부내역 엑셀 다운로드</li>
                                    <li>로켓 마이너스 발주 확인</li>
                                 </ul>

                              </template>
                           </card>
                        </b-col>
                     </b-row>
                  </div>
                  <b-row>
                     <b-col lg="12">
                        <div class="p-3">
                           <h5>주의사항</h5>
                           <span>- 구독결제는 신용카드 결제로만 가능합니다.</span>
                           <br><span>- 신용카드 결제 진행을 위해 개인정보수집 동의 및 약관 동의는 필수입니다.</span>
                           <br><span>- 유효기간은 다음달 같은 날짜까지 입니다. (예시. 23.02.17 결제 시 23.03.17까지
                              유효기간)</span>
                           <br><span>- 무단 사용을 금지하기 위해 특정 IP, 기기에 대해 이용이 차단될 수 있습니다.</span>
                           <br><span>- 슬롯갯수는 최대 사용갯수로 한도 내에서 다른 링크로 바꿔서 사용 가능합니다.</span>
                           <br><span>- 추가 문의사항은 채널톡으로 문의바랍니다.</span>
                           <br><span>- 쿠플러스 채널 : http://pf.kakao.com/_xhDjxeb</span>
                        </div>
                     </b-col>
                     <b-col lg="5">
                        <div class="mt-3 card p-3 ">
                           <div
                              class="d-flex justify-content-start align-items-center text-center">
                              <span class="w-25">카드번호</span>
                              <b-form-input class="w-75 ml-2" v-model="cardNumber" type="number"
                                 @input="validateCardNumber"
                                 @change="confirmcheck"></b-form-input>

                           </div>
                           <div class="d-flex justify-content-start align-items-center text-center mt-2">
                              <span class="w-25">유효기간</span>
                              <b-form-input class="w-25 ml-2" v-model="cardExpirationMonth"
                                 type="number" placeholder="MM" max="12"
                                 @input="validateExpiryDate"
                                 @change="confirmcheck"></b-form-input>
                              <b-form-input class="w-25 ml-2" v-model="cardExpirationYear"
                                 type="number" placeholder="YY" max="99"
                                 @input="validateExpiryDate"
                                 @change="confirmcheck"></b-form-input>
                           </div>

                           <div class="d-flex justify-content-start align-items-center text-center mt-2" v-if="법인카드 == false">
                              <span class="w-25">생년월일</span>
                              <b-form-input class="w-75 ml-2" v-model="customerIdentityNumber"
                                 type="number" @input="validatecustomernumber" placeholder="YYMMDD"
                                 @change="confirmcheck"></b-form-input>
                           </div>

                           <div class="d-flex justify-content-start align-items-center text-center mt-2" v-if="법인카드 == true">
                              <span class="w-25">사업자<br>번호</span>
                              <b-form-input class="w-75 ml-2" v-model="customerIdentityNumber"
                                 type="number" placeholder="사업자번호 10자리"
                                 @input="validatecustomernumber"
                                 @change="confirmcheck"></b-form-input>
                           </div>

                           <div class="d-flex justify-content-start align-items-center text-center mt-2">
                              <div class="custom-control custom-switch custom-control-inline ml-1">
                                 <input type="checkbox" class="custom-control-input" id="customSwitch1" v-model="법인카드" @change="checkcard">
                                 <label class="custom-control-label" for="customSwitch1"><span>법인카드</span></label>
                              </div>
                           </div>

                        </div>
                        <div>
                           <div v-if="cardNumberError" class="error-message">{{ cardNumberError
                           }}</div>
                           <div v-if="expiryDateError" class="error-message">{{ expiryDateError
                           }}</div>
                           <div v-if="birthdateError" class="error-message">{{ birthdateError }}
                           </div>
                        </div>

                     </b-col>
                     <b-col lg="7">
                        <div class="mt-1 p-3">
                           <div
                              class="d-flex justify-content-start align-items-center text-center">
                              <b-form-checkbox v-model="allchecked" @change="allcheck">
                                 <b>모든 약관에 동의합니다.</b>
                              </b-form-checkbox>
                           </div>
                           <hr>
                           <div
                              class="d-flex justify-content-start align-items-center text-center">
                              <b-form-checkbox v-model="check1" @change="confirmcheck">
                                 결제 조건을 확인하였으며, 결제 진행에 동의합니다. <b> (필수) </b>
                              </b-form-checkbox>
                           </div>
                           <div class="d-flex justify-content-between align-items-center">
                              <div class="d-flex align-items-center">
                                 <b-form-checkbox v-model="check2" @change="confirmcheck">
                                    개인정보수집/이용에 동의합니다. <b>(필수)</b>
                                 </b-form-checkbox>
                              </div>
                              <button type="button" class="btn btn-link text-primary p-0" @click="openPrivacyModal" style="text-decoration: underline;">보기</button>
                           </div>
                           <div class="d-flex justify-content-between align-items-center">
                              <div class="d-flex align-items-center">
                                 <b-form-checkbox v-model="check3" @change="confirmcheck">
                                    결제대행 서비스 이용약관에 동의합니다. <b>(필수)</b>
                                 </b-form-checkbox>
                              </div>
                              <button type="button" class="btn btn-link text-primary p-0" @click="openTermsModal" style="text-decoration: underline;">보기</button>
                           </div>
                           <div class="d-flex justify-content-between align-items-center">
                              <div class="d-flex align-items-center">
                                 <b-form-checkbox v-model="check4" @change="confirmcheck">
                                    쿠플러스 서비스 이용약관에 동의합니다. <b>(필수)</b>
                                 </b-form-checkbox>
                              </div>
                              <button type="button" class="btn btn-link text-primary p-0" @click="openServiceModal" style="text-decoration: underline;">보기</button>
                           </div>
                           <div class="d-flex justify-content-end">
                              <button type="button" :class="classapproval" v-b-modal.gopayment @click="makenow">결제하기</button>
                           </div>

                           <b-modal id="gopayment" size="lg" ok-title="결제하기" cancel-title="취소" @ok="payment">
                              <div class="text-center mt-4">
                                 <h5>매월 {{ now }}일에 자동으로 결제됩니다. 계속하시겠습니까?</h5>
                                 <p><small>구독 취소는 로그인 후 우측상단 사용자 클릭 > 구독관리에서 확인 가능합니다.</small></p>
                              </div>
                           </b-modal>

                           <!-- 개인정보수집/이용 동의 모달 -->
                           <b-modal id="privacy" size="lg" ok-title="동의하기" cancel-title="취소" @ok="checkprivacy">
                              <div class="text-center mt-4">
                                 <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h4 class="text-center flex-grow-1">개인정보수집/이용 동의</h4>
                                    <b-button variant="outline-primary" size="sm" @click="downloadPrivacyPDF">
                                       <i class="fas fa-download"></i> PDF 다운로드
                                    </b-button>
                                 </div>
                                 <div id="privacy-content" style="max-height: 400px; overflow-y: auto; text-align: left; font-size: 12px; padding: 20px;">
                                    <p>(주)루비데이터랩은 원활한 서비스제공을 위해 최소한의 범위내에서 아래와 같이 개인정보를 수집, 이용합니다.</p>

                                    <h5><strong>1. 개인정보의 수집·이용 목적</strong></h5>
                                    <p>쿠플러스 사용을 위해 전자 결제 사용 시 아래 목적으로 개인정보 수집 및 이용됩니다.</p>
                                    <ul>
                                       <li>결제수단(신용카드 등)을 이용한 쿠플러스 웹 서비스 제공</li>
                                       <li>전자결제 결과 조회 및 통보</li>
                                       <li>세금계산서 및 현금영수증 발행</li>
                                       <li>결제한 거래의 취소 또는 환불</li>
                                       <li>전자결제 이력 관리 및 민원 대응</li>
                                       <li>이용자 본인확인, 개인식별, 공지사항의 전달</li>
                                       <li>고객 불만 및 민원처리, 분쟁해결</li>
                                       <li>새로운 정보와 고지사항의 안내</li>
                                    </ul>
                                    <p><strong>※</strong> 개인정보 제공에 동의하지 않으실 수 있으며, 동의하지 않으실 경우 서비스 이용이 제한될 수 있습니다. 수집한 개인정보는 위의 목적으로 처리하며, 동의받은 목적 이외의 용도로 처리하지 않습니다.</p>

                                    <h5><strong>2. 수집·이용하는 개인정보 항목</strong></h5>
                                    <p>쿠플러스 사용 목적의 전자 결제 시 아래 개인정보 항목이 수집됩니다.<br>쿠플러스에 회원가입 시 입력한 회원 정보가 사용됩니다.</p>
                                    <p><strong>가. 전자결제 시 수집되는 항목</strong></p>
                                    <ul>
                                       <li>구매자명 또는 구매자ID, 결제금액</li>
                                       <li>이메일주소, 휴대폰번호</li>
                                    </ul>
                                    <p><strong>나. 결제수단별 수집되는 항목</strong></p>
                                    <p><strong>신용카드:</strong> 카드번호, 유효기간, 카드소유자 생년월일(법인카드의 경우 사업자등록번호), 카드비밀번호 앞 2자리</p>
                                    <p><strong>계좌이체:</strong> 은행명, 계좌번호, 예금주명, 예금주 생년월일, 계좌비밀번호</p>
                                    <p><strong>가상계좌:</strong> 입금자명</p>

                                    <h5><strong>3. 개인정보의 보유 및 이용기간</strong></h5>
                                    <p><strong>쿠플러스 계정 탈퇴시까지</strong>, 단, 전자상거래 등에서의 소비자보호에 관한 법률 등 관련 법령의 규정에 따라 거래 관계 확인을 위해 개인정보를 일정기간 보유할 수 있습니다.</p>
                                    <ul>
                                       <li>계약 또는 청약철회 등에 관한 기록: 5년</li>
                                       <li>대금결제 및 재화 등의 공급에 관한 기록: 5년</li>
                                       <li>소비자의 불만 또는 분쟁처리에 관한 기록: 3년</li>
                                       <li>표시·광고에 관한 기록: 6개월</li>
                                    </ul>

                                    <h5><strong>4. 개인정보 수집·이용 동의 거부권</strong></h5>
                                    <p>이용자는 개인정보의 수집·이용에 대한 동의를 거부할 권리가 있습니다. 다만, 개인정보 수집·이용에 대한 동의를 거부할 경우 전자결제 서비스의 이용이 제한될 수 있음을 알려드립니다.</p>
                                 </div>
                              </div>
                           </b-modal>

                           <!-- 결제대행 서비스 이용약관 모달 -->
                           <b-modal id="terms" size="lg" ok-title="동의하기" cancel-title="취소" @ok="checkterms">
                              <div>
                                 <div class="d-flex justify-content-between align-items-center mb-3">
                                    <div class="d-flex justify-content-center flex-grow-1">
                                       <b-form-group>
                                          <b-form-radio-group v-model="selectedradio" :options="options"
                                             button-variant="outline-primary" size="lg" buttons
                                             @change="contentchange"></b-form-radio-group>
                                       </b-form-group>
                                    </div>
                                    <b-button variant="outline-primary" size="sm" @click="downloadTermsPDF">
                                       <i class="fas fa-download"></i> PDF 다운로드
                                    </b-button>
                                 </div>
                                 <div style="outline:none" tabindex="-1">
                                    <div class="p-container p-container--default">
                                       <div class="p-container__inner p-5">
                                          <header class="text-center">
                                             <img width="150" src="https://static.toss.im/icons/png/4x/logo-payments-short-blue.png">
                                          </header>
                                          <article id="terms-content" v-if="showradio1" style="font-size:80%" class="mt-3 p-post text--left">
                                             <h3>전자금융거래 기본약관(이용자용)</h3>
                                             <h4>제1조 (목적)</h4>
                                             <p>이 약관은 전자지급결제대행서비스 및 결제대금예치서비스를 제공하는 토스페이먼츠 주식회사(이하 '회사'라 합니다)와 이용자 사이의 전자금융거래에 관한 기본적인 사항을 정함으로써 전자금융거래의 안정성과 신뢰성을 확보함에 그 목적이 있습니다.</p>
                                             <h4>제2조 (용어의 정의)</h4>
                                             <p>이 약관에서 정하는 용어의 정의는 다음과 같습니다.</p>
                                             <p>① 전자금융거래'라 함은 회사가 전자적 장치를 통하여 전자지급결제대행서비스 및 결제대금예치서비스(이하 '전자금융거래 서비스'라고 합니다)를 제공하고, 이용자가 회사의 구성원과 직접 대면하거나 의사소통을 하지 아니하고 전산화된 방식으로 이를 이용하는 거래를 말합니다.</p>
                                             <p>② '전자지급결제대행 서비스'라 함은 전자적 방법으로 재화의 구입 또는 용역의 이용에 있어서 지급결제정보를 송신하거나 수신하는 것 또는 그 대가의 정산을 대행하거나 매개하는 서비스를 말합니다.</p>
                                             <p>③ '결제대금예치서비스'라 함은 이용자가 재화의 구입 또는 용역의 이용에 있어서 그 대가(이하 '결제대금'이라 한다)의 전부 또는 일부를 재화 또는 용역(이하 '재화 등'이라 합니다)을 공급받기 전에 미리 지급하는 경우, 회사가 이용자의 물품수령 또는 서비스 이용 확인 시점까지 결제대금을 예치하는 서비스를 말합니다.</p>
                                             <p>④ '이용자'라 함은 이 약관에 동의하고 회사가 제공하는 전자지급결제대행 서비스를 이용하는 자를 말합니다.</p>
                                             <p>⑤ '접근매체'라 함은 전자금융거래에 있어서 거래지시를 하거나 이용자 및 거래내용의 진실성과 정확성을 확보하기 위하여 사용되는 수단 또는 정보로서 전자식 카드 및 이에 준하는 전자적 정보(신용카드번호를 포함한다), '전자서명법'상의 인증서, 금융기관 또는 전자금융업자에 등록된 이용자번호, 이용자의 생체정보, 이상의 수단이나 정보를 사용하는데 필요한 비밀번호 등 전자금융거래법 제2조 제10호에서 정하고 있는 것을 말합니다.</p>
                                             <p>⑥ '거래지시'라 함은 이용자가 전자금융거래계약에 따라 금융기관 또는 전자금융업자에게 전자금융거래의 처리를 지시하는 것을 말합니다.</p>
                                             <p>⑦ '오류'라 함은 이용자의 고의 또는 과실 없이 전자금융거래가 전자금융거래계약 또는 이용자의 거래지시에 따라 이행되지 아니한 경우를 말합니다.</p>
                                             <h4>제3조 (약관의 명시 및 변경)</h4>
                                             <p>① 회사는 이용자가 전자지급결제대행 서비스를 이용하기 전에 이 약관을 게시하고 이용자가 이 약관의 중요한 내용을 확인할 수 있도록 합니다.</p>
                                             <p>② 회사는 이용자의 요청이 있는 경우 서면제공 방식 또는 전자문서의 전송방식에 의하여 본 약관의 사본을 이용자에게 교부합니다.</p>
                                             <p>③ 회사가 약관을 변경하는 때에는 그 시행일 1월 전에 변경되는 약관을 지급결제정보 입력화면 및 회사의 홈페이지에 게시함으로써 이용자에게 공지합니다.</p>
                                             <p>④ 이용자는 제3항의 규정에 따른 약관의 변경내용이 게시되거나 통지된 후부터 변경되는 약관의 시행일 전의 영업일까지 전자금융거래의 계약을 해지할 수 있습니다. 전단의 기간 안에 이용자가 약관의 변경내용에 대하여 이의를 제기하지 아니하는 경우에는 약관의 변경을 승인한 것으로 봅니다.</p>
                                             <h4>제4조 (전자지급결제대행서비스의 종류)</h4>
                                             <p>회사가 제공하는 전자지급결제대행서비스는 지급결제수단에 따라 다음과 같이 구별됩니다.</p>
                                             <p>① 신용카드결제대행서비스: 이용자가 결제대금의 지급을 위하여 제공한 지급결제수단이 신용카드인 경우로서, 회사가 전자결제시스템을 통한 신용카드 지불정보의 송,수신 및 결제대금의 정산을 대행하거나 매개하는 서비스를 말합니다.</p>
                                             <p>② 계좌이체대행서비스: 이용자가 결제대금을 회사의 전자결제시스템을 통하여 금융기관에 등록한 자신의 계좌에서 출금하여 원하는 계좌로 이체할 수 있는 실시간 송금 서비스를 말합니다.</p>
                                             <p>③ 가상계좌서비스: 이용자가 결제대금을 현금으로 결제하고자 경우 회사의 전자결제시스템을 통하여 자동으로 이용자만의 고유한 일회용 계좌의 발급을 통하여 결제대금의 지급이 이루어지는 서비스를 말합니다.</p>
                                             <p>④ 기타: 회사가 제공하는 서비스로서 지급결제수단의 종류에 따라 '휴대폰 결제대행서비스', 'KT전화(ARS,폰빌)결제대행서비스', '상품권결제대행서비스'등이 있습니다.</p>
                                             <h4>제5조 (결제대금예치서비스의 내용)</h4>
                                             <p>① 이용자(이용자의 동의가 있는 경우에는 재화 등을 공급받을 자를 포함합니다. 이하 본조에서 같습니다)는 재화 등을 공급받은 사실을 재화 등을 공급받은 날부터 3영업일 이내에 회사에 통보하여야 합니다.</p>
                                             <p>② 회사는 이용자로부터 재화 등을 공급받은 사실을 통보 받은 후 회사와 통신판매업자간 사이에서 정한 기일 내에 통신판매업자에게 결제대금을 지급합니다.</p>
                                             <p>③ 회사는 이용자가 재화 등을 공급받은 날부터 3영업일이 지나도록 정당한 사유의 제시 없이 그 공급받은 사실을 회사에 통보하지 아니하는 경우에는 이용자의 동의 없이 통신판매업자에게 결제대금을 지급할 수 있습니다.</p>
                                             <p>④ 회사는 통신판매업자에게 결제대금을 지급하기 전에 이용자에게 결제대금을 환급받을 사유가 발생한 경우에는 그 결제대금을 소비자에게 환급합니다.</p>
                                             <p>⑤ 회사는 이용자와의 결제대금예치서비스 이용과 관련된 구체적인 권리,의무를 정하기 위하여 본 약관과는 별도로 결제대금예치서비스이용약관을 제정할 수 있습니다.</p>
                                             <h4>제6조 (접근매체의 선정, 관리 등)</h4>
                                             <p>① 회사는 전자지급결제대행 서비스 제공 시 접근매체를 선정하여 이용자의 신원, 권한 및 거래지시의 내용 등을 확인할 수 있습니다.</p>
                                             <p>② 이용자는 접근매체를 제3자에게 대여하거나 사용을 위임하거나 양도 또는 담보 목적으로 제공할 수 없습니다.</p>
                                             <p>③ 이용자는 자신의 접근매체를 제3자에게 누설 또는 노출하거나 방치하여서는 안되며, 접근매체의 도용이나 위조 또는 변조를 방지하기 위하여 충분한 주의를 기울여야 합니다.</p>
                                             <p>④ 회사는 이용자로부터 접근매체의 분실이나 도난 등의 통지를 받은 때에는 그 때부터 제3자가 그 접근매체를 사용함으로 인하여 이용자에게 발생한 손해를 배상할 책임이 있습니다.</p>
                                             <h4>제7조 (회사의 책임)</h4>
                                             <p>① 접근매체의 위조나 변조로 발생한 사고로 인하여 이용자에게 발생한 손해에 대하여 배상책임이 있습니다. 다만, 이용자가 접근매체를 제3자에게 대여하거나 그 사용을 위임한 경우 또는 양도나 담보의 목적으로 제공한 경우, 회사가 보안강화를 위하여 전자금융거래 시 요구하는 추가적인 보안조치를 이용자가 정당한 사유 없이 거부하여 정보통신망에 침입하여 거짓이나 그 밖의 부정한 방법으로 획득한 접근매체의 이용으로 사고가 발생한 경우, 이용자가 제6조 제2항에 위반하거나 제3자가 권한 없이 이용자의 접근매체를 이용하여 전자금융거래를 할 수 있음을 알았거나 알 수 있었음에도 불구하고 이용자가 자신의 접근매체를 누설 또는 노출하거나 방치한 경우 그 책임의 전부 또는 일부를 이용자가 부담하게 할 수 있습니다.</p>
                                             <p>② 회사는 계약체결 또는 거래지시의 전자적 전송이나 처리과정에서 발생한 사고로 인하여 이용자에게 손해가 발생한 경우에는 그 손해를 배상할 책임이 있습니다. 다만, 본조 제1항 단서에 해당하거나 법인('중소기업기본법' 제2조 제2항에 의한 소기업을 제외한다)인 이용자에게 손해가 발생한 경우로서 회사가 사고를 방지하기 위하여 보안절차를 수립하고 이를 철저히 준수하는 등 합리적으로 요구되는 충분한 주의의무를 다한 경우 그 책임의 전부 또는 일부를 이용자가 부담하게 할 수 있습니다.</p>
                                             <p>③ 회사는 전자금융거래를 위한 전자적 장치 또는 '정보통신망 이용촉진 및 정보보호 등에 관한 법률' 제2조제1항제1호에 따른 정보통신망에 침입하여 거짓이나 그 밖의 부정한 방법으로 획득한 접근매체의 이용으로 발생한 사고로 인하여 이용자에게 그 손해가 발생한 경우에는 그 손해를 배상할 책임이 있습니다.</p>
                                             <h4>제8조 (거래내용의 확인)</h4>
                                             <p>① 회사는 이용자와 미리 약정한 전자적 방법을 통하여 이용자의 거래내용(이용자의 '오류정정 요구사실 및 처리결과에 관한 사항'을 포함합니다)을 확인할 수 있도록 하며, 이용자의 요청이 있는 경우에는 요청을 받은 날로부터 2주 이내에 전자적양식, 모사전송, 서면 등의 방법으로 거래내용에 관한 서면을 교부합니다. 전자적 장치의 운영장애, 그 밖의 사유로 거래내용을 확인하게 할 수 없는 때에는 인터넷 등을 이용하여 즉시 그 사유를 알리고, 그 사유가 종료된 때부터 이용자가 거래내용을 확인할 수 있도록 하여야 합니다.</p>
                                             <p>② 회사가 이용자에게 제공하는 거래내용 중 거래계좌의 명칭 또는 번호, 거래의 종류 및 금액, 거래상대방을 나타내는 정보, 거래일자, 전자적 장치의 종류 및 전자적 장치를 식별할 수 있는 정보와 해당 전자금융거래와 관련한 전자적 장치의 접속기록은 5년간, 건당 거래금액이 1만원 이하인 소액 전자금융거래에 관한 기록, 전자지급수단 이용시 거래승인에 관한 기록, 이용자의 오류정정 요구사실 및 처리결과에 관한 사항은 1년간의 기간을 대상으로 하되, 회사가 가맹점에 대한 전자지급결제대행 서비스 제공의 대가로 수취한 수수료에 관한 사항은 제공하는 거래내용에서 제외됩니다.</p>
                                          </article>
                                          <article v-if="showradio2" style="font-size:80%" class="mt-3 p-post text--left">
                                             <h3>개인정보 수집이용 동의</h3>
                                             <p>토스페이먼츠(이하 회사라 함)는 "신용정보의 이용 및 보호에 관한 법률", "전자금융거래법", "전자상거래 등에서의 소비자보호에 관한 법률", "정보통신망 이용촉진 및 정보보호 등에 관한 법률" 및 "개인정보 보호법" 등 관련 법령을 준수하여, 이용자 권익 보호에 최선을 다하고 있습니다.</p>
                                             <h4>1. 개인정보의 수집·이용 목적</h4>
                                             <p>가. 전자금융거래 서비스 제공: 전자결제지급대행, 자체 간편결제 및 오픈뱅킹 등</p>
                                             <ul>
                                                <li>결제수단(신용카드 등)을 이용한 전자결제 서비스 제공</li>
                                                <li>전자결제 결과 조회 및 통보</li>
                                                <li>세금계산서 및 현금영수증 발행</li>
                                                <li>결제한 거래의 취소 또는 환불, 상품 배송 등 전자상거래 관련 서비스 제공</li>
                                             </ul>
                                             <p>나. 서비스 이용자 관리 및 민원대응</p>
                                             <ul>
                                                <li>이용자 본인확인, 개인식별, 공지사항의 전달, 고객 불만 및 민원처리 등</li>
                                                <li>금융사고 조사, 분쟁해결, 민원처리, 법령상 의무이행</li>
                                                <li>새로운 정보와 고지사항의 안내</li>
                                             </ul>
                                             <p><strong>※ 회사는 개인정보를 위의 목적으로 처리하며, 동의받은 목적 이외의 용도로 처리하지 않습니다.</strong></p>
                                             <h4>2. 수집·이용하는 개인정보 항목</h4>
                                             <p>가. 전자결제 시 수집되는 항목</p>
                                             <ul>
                                                <li>구매자명 또는 구매자ID, 결제금액</li>
                                             </ul>
                                             <p>나. 결제수단별 수집되는 항목</p>
                                             <p><strong>신용카드:</strong> 카드번호, 유효기간, 카드소유자 생년월일(법인카드의 경우 사업자등록번호), 카드비밀번호 앞 2자리</p>
                                             <p><strong>계좌이체:</strong> 은행명, 계좌번호, 예금주명, 예금주 생년월일, 계좌비밀번호</p>
                                             <p><strong>가상계좌:</strong> 입금자명</p>
                                          </article>
                                          <article v-if="showradio3" style="font-size:80%" class="mt-3 p-post text--left">
                                             <h3>개인정보 제3자 제공 동의</h3>
                                             <p>토스페이먼츠(이하 회사라 함)는 "신용정보의 이용 및 보호에 관한 법률", "전자금융거래법", "전자상거래 등에서의 소비자보호에 관한 법률", "정보통신망 이용촉진 및 정보보호 등에 관한 법률" 및 "개인정보 보호법" 등 관련 법령을 준수하여, 이용자 권익 보호에 최선을 다하고 있습니다.</p>
                                             <h4>1. 개인정보의 제3자 제공</h4>
                                             <p>회사는 전자결제지급대행 서비스 제공을 위해 아래와 같이 개인정보를 제3자에게 제공하고 있습니다.</p>
                                             <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
                                                <thead>
                                                   <tr style="background-color: #f5f5f5;">
                                                      <th style="border: 1px solid #ddd; padding: 8px;"><strong>제공받는 자</strong></th>
                                                      <th style="border: 1px solid #ddd; padding: 8px;"><strong>제공목적</strong></th>
                                                      <th style="border: 1px solid #ddd; padding: 8px;"><strong>제공항목</strong></th>
                                                      <th style="border: 1px solid #ddd; padding: 8px;"><strong>보유 및 이용기간</strong></th>
                                                   </tr>
                                                </thead>
                                                <tbody>
                                                   <tr>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">카드사, 은행 등 결제수단 발행업체</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">결제 승인 및 정산</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">카드번호, 유효기간, 카드소유자 생년월일 등</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">결제 완료 후 즉시 파기</td>
                                                   </tr>
                                                   <tr>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">가맹점</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">결제 결과 통보</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">거래승인번호, 결제금액, 결제일시</td>
                                                      <td style="border: 1px solid #ddd; padding: 8px;">서비스 제공 완료 시까지</td>
                                                   </tr>
                                                </tbody>
                                             </table>
                                             <p><strong>※ 회사는 개인정보를 위의 목적으로 제3자 제공하며, 동의받은 목적 이외의 제3자에게 제공하지 처리하지 않습니다.</strong></p>
                                             <h4>2. 개인정보의 보유 및 이용기간</h4>
                                             <p>개인정보는 원칙적으로 개인정보의 수집·이용 목적이 달성되면 지체없이 파기합니다. 단, 다른 법령에 특별한 규정이 있는 경우 관련 법령에서 정하는 기간동안 보유합니다.</p>
                                             <h4>3. 이용자는 회사의 개인정보 제3자 제공에 대한 동의를 거부할 권리가 있습니다.</h4>
                                             <p>다만, 개인정보의 제3자 제공 동의를 거부할 경우 전자결제지급대행 서비스의 이용이 제한될 수 있음을 알려드립니다.</p>
                                          </article>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </b-modal>

                           <!-- 쿠플러스 서비스 이용약관 모달 -->
                           <b-modal id="service" size="lg" ok-title="동의하기" cancel-title="취소" @ok="checkservice">
                              <div>
                                 <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h4 class="text-center flex-grow-1">쿠플러스 서비스 이용약관</h4>
                                    <b-button variant="outline-primary" size="sm" @click="downloadServicePDF">
                                       <i class="fas fa-download"></i> PDF 다운로드
                                    </b-button>
                                 </div>
                                 <div id="service-content" style="max-height: 400px; overflow-y: auto; text-align: left; font-size: 12px; padding: 20px;">
                                    <h5><strong>제1조 (목적)</strong></h5>
                                    <p>이 약관은 (주)루비데이터랩(이하 "회사"라 함)이 제공하는 쿠플러스 서비스(이하 "서비스"라 함)의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.</p>

                                    <h5><strong>제2조 (용어의 정의)</strong></h5>
                                    <p>① "서비스"란 회사가 제공하는 쿠플러스 웹 서비스 및 관련 부가 서비스를 의미합니다.</p>
                                    <p>② "이용자"란 본 약관에 동의하고 회사가 제공하는 서비스를 이용하는 자를 말합니다.</p>
                                    <p>③ "구독"이란 이용자가 서비스 이용을 위해 정기적으로 이용료를 지불하는 것을 말합니다.</p>

                                    <h5><strong>제3조 (약관의 효력 및 변경)</strong></h5>
                                    <p>① 본 약관은 서비스 화면에 게시하거나 기타의 방법으로 이용자에게 공지함으로써 효력이 발생합니다.</p>
                                    <p>② 회사는 관련 법령을 위배하지 않는 범위 내에서 본 약관을 변경할 수 있으며, 변경 시 적용일자 및 변경사유를 명시하여 현행 약관과 함께 서비스 화면에 그 적용일자 7일 전부터 공지합니다.</p>

                                    <h5><strong>제4조 (서비스의 제공)</strong></h5>
                                    <p>① 회사가 제공하는 서비스의 내용은 다음과 같습니다.</p>
                                    <ul>
                                       <li>쿠팡 상품 데이터 분석 서비스</li>
                                       <li>키워드 분석 및 추적 서비스</li>
                                       <li>매출 관리 및 통계 서비스</li>
                                       <li>기타 회사가 추가 개발하거나 제휴를 통해 제공하는 서비스</li>
                                    </ul>
                                    <p>② 회사는 서비스의 내용을 변경할 수 있으며, 변경 시 이용자에게 사전 공지합니다.</p>

                                    <h5><strong>제5조 (서비스 이용료)</strong></h5>
                                    <p>① 서비스 이용료는 서비스 화면에 게시된 금액을 기준으로 합니다.</p>
                                    <p>② 구독 결제는 매월 자동으로 이루어지며, 이용자는 구독관리에서 언제든지 구독을 취소할 수 있습니다.</p>
                                    <p>③ 구독 취소 시 이미 결제된 기간까지는 서비스를 이용할 수 있습니다.</p>

                                    <h5><strong>제6조 (이용자의 의무)</strong></h5>
                                    <p>① 이용자는 서비스 이용 시 관련 법령 및 본 약관의 규정을 준수하여야 합니다.</p>
                                    <p>② 이용자는 서비스를 제3자에게 양도하거나 공유할 수 없습니다.</p>
                                    <p>③ 이용자는 서비스를 이용하여 얻은 정보를 무단으로 복제, 배포, 판매할 수 없습니다.</p>

                                    <h5><strong>제7조 (면책사항)</strong></h5>
                                    <p>① 회사는 천재지변 또는 이에 준하는 불가항력으로 인해 서비스를 제공할 수 없는 경우에는 책임이 면제됩니다.</p>
                                    <p>② 회사는 이용자의 귀책사유로 인한 서비스 이용 장애에 대하여 책임을 지지 않습니다.</p>
                                    <p>③ 서비스에서 제공하는 데이터는 참고용이며, 이를 기반으로 한 이용자의 의사결정에 대해 회사는 책임을 지지 않습니다.</p>
                                 </div>
                              </div>
                           </b-modal>

                        </div>

                     </b-col>
                  </b-row>


               </form>
            </div>
         </b-card>
      </b-modal>
   </section>
</template>

<script>
import axios from 'axios'
import ledgerCache from '@/services/ledgerCache'
// import { loadTossPayments } from '@tosspayments/payment-sdk'


// async/await을 사용하는 경우


export default {
   name: 'SignInModern',
   data() {
      return {
         now:'',
         allchecked: false,
         check1: false,
         check2: false,
         check3: false,
         check4: false,
         clientKey: 'test_ck_XjExPeJWYVQwZyOo7ng849R5gvNL',
         tossPayments: '',
         companyid: "",
         email: "",
         pw: "",
         isLoading: false,
         errorMessage: '',
         rememberMe: false,
         showspinner: false,
         selected: 'lite',
         // 요금표 — 서버(GET /api/login/plan-prices)가 정본. 받기 전·실패 시에만 이 값(서버가 틀리면 결제를 거절한다)
         planPrices: { 'COUPLUS-Lite': 49000, 'COUPLUS-Premium': 290000 },
         customerKey: '',
         cardNumber: '',
         cardExpirationYear: '',
         cardExpirationMonth: '',
         customerIdentityNumber: '',
         // cardPassword: '',
         cardNumberError: '',
         expiryDateError: '',
         birthdateError: '',
         // cardPasswordError: '',
         // 무료 이용권 — 로그인 응답이 알려준다(미승인 상태라 따로 조회할 길이 마땅찮다).
         coupon: { available: false, days: 0 },
         couponBusy: false,
         couponError: '',
         couponUsing: false,
         classapproval: 'btn btn-outline-light mt-3 disabled',
         selectedradio: 'radio1',
         options: [
            { text: '기본약관', value: 'radio1' },
            { text: '개인정보 수집 이용 동의', value: 'radio2' },
            { text: '개인정보 제 3자 제공 동의', value: 'radio3' },
         ],
         showradio1: true,
         showradio2: false,
         showradio3: false,
         법인카드:false,
      }
   },
   mounted() {
      this.loadPlanPrices();
      // 세션 만료 안내
      if (this.$route.query.expired === 'true') {
         this.errorMessage = '세션이 만료되었습니다. 다시 로그인해 주세요.';
      }

      // 로그인 상태 유지 확인
      const rememberLogin = localStorage.getItem('rememberLogin');
      const savedEmail = localStorage.getItem('userEmail');

      if (rememberLogin === 'true' && savedEmail) {
         this.email = savedEmail;
         this.rememberMe = true;
      }
   },
   methods: {
      async loadPlanPrices() {
         try {
            const r = await axios.get('/api/login/plan-prices');
            if (r.data && r.data.plans) this.planPrices = Object.assign({}, this.planPrices, r.data.plans);
         } catch (e) {
            console.error('요금표 조회 실패', e);
         }
      },
      planPriceText(name) {
         const n = Number(this.planPrices[name]);
         return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '-';
      },
      checkcard(){
         this.customerIdentityNumber = ""
      },
      makenow(){
         var today = new Date();
         this.now = today.getDate();
      },
      validateCardNumber() {
         const regex = /^[0-9]{15,16}$/;
         if (!regex.test(this.cardNumber) && this.cardNumber) {
            this.cardNumberError = '카드번호를 올바르게 입력해주세요.';
         } else {
            this.cardNumberError = '';
         }
      },
      validateExpiryDate() {
         const yearRegex = /^\d{2}$/;
         const monthRegex = /^(0[1-9]|1[0-2])$/;
         if (this.cardExpirationMonth && this.cardExpirationYear) {
            if (!yearRegex.test(this.cardExpirationYear) || !monthRegex.test(this.cardExpirationMonth)) {
               this.expiryDateError = '카드 유효기간을 올바르게 입력해주세요.';
            } else {
               this.expiryDateError = '';
            }
         } else {
            this.expiryDateError = '';
         }

      },
      validatecustomernumber() {
         const regex1 = /^[0-9]{6}$/;
         const regex2 = /^[0-9]{10}$/;
         if(this.법인카드){
            if (!regex2.test(this.customerIdentityNumber) && this.customerIdentityNumber) {
               this.birthdateError = '사업자번호 10자리를 올바르게 입력해주세요.';
            } else {
               this.birthdateError = '';
            }
         } else {
            if (!regex1.test(this.customerIdentityNumber) && this.customerIdentityNumber) {
               this.birthdateError = '생년월일 6자리를 올바르게 입력해주세요.';
            } else {
               this.birthdateError = '';
            }
         }
      },
      async payment() {
         var now = Date.now();
         var order = 'COUPLUS_' + now + "_"
         axios.post('/api/login/payment', {

            email: this.email,
            data: {
               cardNumber: this.cardNumber,
               cardExpirationYear: this.cardExpirationYear,
               cardExpirationMonth: this.cardExpirationMonth,
               customerIdentityNumber: this.customerIdentityNumber.toString().replaceAll("-",""),
               customerKey: this.customerKey,
               email: this.email,
               amount : String(this.planPrices[this.selected == 'lite' ? 'COUPLUS-Lite' : 'COUPLUS-Premium']),
               orderId : order,
               orderName : this.selected == 'lite' ? 'COUPLUS-Lite' : 'COUPLUS-Premium'
            }
         })
         .then(result => {
            if(result.data.status !== 200){
               alert(result.data.message);
               return
            }
            console.log(result.data)
            if(result.data.status == 200 && result.data.code == 'ok'){
               this.onSubmit();
            } else {
               console.log(result.data)
               alert("로그인 실패")
            }
         }).catch((e) => {
            this.showspinner = false;
            alert("로그인 실패")
            console.log(e);
         })
      },
      contentchange() {
         if (this.selectedradio == 'radio1') {
            this.showradio1 = true;
            this.showradio2 = false;
            this.showradio3 = false;
         }
         if (this.selectedradio == 'radio2') {
            this.showradio1 = false;
            this.showradio2 = true;
            this.showradio3 = false;
         }
         if (this.selectedradio == 'radio3') {
            this.showradio1 = false;
            this.showradio2 = false;
            this.showradio3 = true;
         }
      },
      checkprivacy() {
         this.check2 = true;
         this.confirmcheck();
      },
      checkterms() {
         this.check3 = true;
         this.confirmcheck();
      },
      checkservice() {
         this.check4 = true;
         this.confirmcheck();
      },
      openPrivacyModal() {
         this.$bvModal.show('privacy');
      },
      openTermsModal() {
         this.$bvModal.show('terms');
      },
      openServiceModal() {
         this.$bvModal.show('service');
      },
      downloadPrivacyPDF() {
         const element = document.getElementById('privacy-content');
         const printWindow = window.open('', '_blank');
         printWindow.document.write(`
            <html>
               <head>
                  <title>개인정보수집/이용 동의</title>
                  <style>
                     body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
                     h3, h4, h5 { color: #333; margin-top: 20px; }
                     p { line-height: 1.6; margin: 10px 0; }
                     ul { margin: 10px 0; padding-left: 20px; }
                     li { margin: 5px 0; }
                     @media print {
                        body { margin: 0; }
                        @page { margin: 1cm; }
                     }
                  </style>
               </head>
               <body>
                  <h2>개인정보수집/이용 동의</h2>
                  ${element.innerHTML}
               </body>
            </html>
         `);
         printWindow.document.close();
         printWindow.focus();
         setTimeout(() => {
            printWindow.print();
         }, 250);
      },
      downloadTermsPDF() {
         const element = document.getElementById('terms-content');
         const printWindow = window.open('', '_blank');
         printWindow.document.write(`
            <html>
               <head>
                  <title>전자금융거래 기본약관</title>
                  <style>
                     body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
                     h3, h4, h5 { color: #333; margin-top: 20px; }
                     p { line-height: 1.6; margin: 10px 0; }
                     ul { margin: 10px 0; padding-left: 20px; }
                     li { margin: 5px 0; }
                     @media print {
                        body { margin: 0; }
                        @page { margin: 1cm; }
                     }
                  </style>
               </head>
               <body>
                  <h2>전자금융거래 기본약관(이용자용)</h2>
                  ${element.innerHTML}
               </body>
            </html>
         `);
         printWindow.document.close();
         printWindow.focus();
         setTimeout(() => {
            printWindow.print();
         }, 250);
      },
      downloadServicePDF() {
         const element = document.getElementById('service-content');
         const printWindow = window.open('', '_blank');
         printWindow.document.write(`
            <html>
               <head>
                  <title>쿠플러스 서비스 이용약관</title>
                  <style>
                     body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
                     h3, h4, h5 { color: #333; margin-top: 20px; }
                     p { line-height: 1.6; margin: 10px 0; }
                     ul { margin: 10px 0; padding-left: 20px; }
                     li { margin: 5px 0; }
                     @media print {
                        body { margin: 0; }
                        @page { margin: 1cm; }
                     }
                  </style>
               </head>
               <body>
                  <h2>쿠플러스 서비스 이용약관</h2>
                  ${element.innerHTML}
               </body>
            </html>
         `);
         printWindow.document.close();
         printWindow.focus();
         setTimeout(() => {
            printWindow.print();
         }, 250);
      },
      confirmerror() {
         if (!this.cardNumberError && !this.expiryDateError && !this.birthdateError && this.cardNumber && this.cardExpirationYear && this.cardExpirationMonth && this.customerIdentityNumber) {
            return true
         } else {
            return false
         }
      },
      confirmcheck() {
         if (this.check1 && this.check2 && this.check3 && this.check4) {
            this.allchecked = true;
            if (this.confirmerror()) {
               this.classapproval = 'btn btn-success mt-3'
            } else {
               this.classapproval = 'btn btn-outline-light mt-3 disabled';
            }
         } else {
            this.allchecked = false;
            this.classapproval = 'btn btn-outline-light mt-3 disabled';
         }
      },
      // 무료 이용권 사용. 성공하면 onSubmit 을 다시 돌려 평소 로그인 경로를 그대로 탄다
      // (스토어 초기화·userupdate·대시보드 이동을 여기서 또 쓰지 않으려는 것이다).
      async useCoupon() {
         if (this.couponBusy) return;
         this.couponBusy = true;
         this.couponError = '';
         try {
            const res = await axios.post('/api/coupon/activate', {});
            if (res.data && res.data['요청결과'] === 'ok') {
               this.coupon = { available: false, days: 0 };
               this.$bvModal.hide('modal-reg');
               this.couponUsing = true;
               this.$bvModal.hide('modal-coupon');
               this.onSubmit();
            } else {
               this.couponError = (res.data && res.data.error) || '이용권을 사용하지 못했습니다.';
            }
         } catch (e) {
            console.error('[이용권] 사용 실패', e);
            this.couponError = '이용권을 사용하지 못했습니다. 잠시 후 다시 시도해주세요.';
         } finally {
            this.couponBusy = false;
         }
      },
      // 승인된 계정이 이용권을 지금 안 쓰면 평소처럼 대시보드로 간다. 다음 로그인 때 다시 묻는다.
      skipCoupon() {
         this.$bvModal.hide('modal-coupon');
      },
      // 창이 닫히면(나중에·X·ESC) 대시보드로 — 사용 성공으로 닫힐 때는 onSubmit 이 다시 로그인해 이동한다.
      onCouponModalHide() {
         if (this.couponUsing) {
            this.couponUsing = false;
            return;
         }
         this.$router.push({ name: 'layout.dashboard1' });
      },
      allcheck() {
         if (this.allchecked) {
            this.check1 = true;
            this.check2 = true;
            this.check3 = true;
            this.check4 = true;
         } else {
            this.check1 = false;
            this.check2 = false;
            this.check3 = false;
            this.check4 = false;
         }
         this.confirmcheck()
      },
      onSubmit() {
         this.$store.commit('resetState');
         this.isLoading = true;
         this.errorMessage = '';

         axios.post('/api/login', {
            email: this.email,
            pw: this.pw,
         })
            .then(async result => {
               if (result.data.loginstatus == "Login success") {
                  // 로그인은 화면만 바꿀 뿐 새로고침이 없다. 지우지 않으면 앞 계정의
                  // 상품DB·발주·재고가 메모리에 남아, 새 계정 매출에 앞 계정 매입가가 섞인다
                  this.$store.commit('resetState');
                  await ledgerCache.clearAll();

                  this.$store.commit('userupdate', result.data.userinfo);
                  this.$store.commit('dbupdate', result.data.dbs);

                  this.isLoading = false;

                  // 로그인 상태 유지 처리
                  if (this.rememberMe) {
                     localStorage.setItem('rememberLogin', 'true');
                     localStorage.setItem('userEmail', this.email);
                  }

                  // 이미 승인된 계정이라도 무료 이용권이 있으면 먼저 묻는다(쓰면 AI 로켓등록이 덧붙는다).
                  if (result.data.coupon && result.data.coupon.available) {
                     this.coupon = result.data.coupon;
                     this.couponError = '';
                     this.couponUsing = false;
                     this.$bvModal.show('modal-coupon');
                     return;
                  }

                  // 로그인 후 대시보드로 리다이렉트 (권한 관계없이)
                  this.$router.push({ name: 'layout.dashboard1' })

               } else {
                  this.isLoading = false;

                  if (result.data.loginstatus == '가입승인 대기중입니다. 관리자에게 문의해 주세요') {
                     this.customerKey = result.data.userinfo._id;
                     this.coupon = result.data.coupon || { available: false, days: 0 };
                     this.couponError = '';
                     this.$bvModal.show('modal-reg');
                  } else {
                     this.errorMessage = result.data.loginstatus || '로그인에 실패했습니다.';
                  }

                  this.$store.commit('userupdate', this.$store.state.userinit);
               }

            }).catch((e) => {
               this.isLoading = false;
               this.errorMessage = '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
               console.log(e);
            })
      },
      submitForm() {
         // 폼 제출 로직 추가
         console.log('카드 번호:', this.cardNumber);
         console.log('카드 유효기간:', this.expiryDate);
         console.log('카드 소유자 생년월일:', this.birthday);
         this.closeModal();
      }

   }
}
</script>

<style scoped>
/* Base Variables & Reset */
.modern-login-content {
   margin: 0;
   padding: 0;
   background: #1a1a2e;
   min-height: 100vh;
   display: flex;
   overflow: hidden;
}

.login-wrapper {
   display: flex;
   width: 100%;
   height: 100vh;
}

/* =======================================
   Left Visual Section (60%)
======================================= */
.visual-section {
   flex: 0 0 60%;
   position: relative;
   display: flex;
   align-items: center;
   justify-content: center;
   overflow: hidden;
   background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
}

.visual-background {
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   z-index: 1;
}

.bg-svg {
   width: 100%;
   height: 100%;
}

/* Animations for SVG elements */
@keyframes floatUp {
   0% { transform: translateY(100px) scale(0.8); opacity: 0; }
   50% { opacity: 1; }
   100% { transform: translateY(-100px) scale(1.2); opacity: 0; }
}

@keyframes floatChart {
   0%, 100% { transform: translateY(0); }
   50% { transform: translateY(-15px); }
}

@keyframes rotateCube {
   0% { transform: rotate(0deg) translateY(0); }
   50% { transform: rotate(10deg) translateY(-20px); }
   100% { transform: rotate(0deg) translateY(0); }
}

.particle {
   animation: floatUp 15s infinite linear;
}

.p1 { animation-delay: 0s; animation-duration: 12s; }
.p2 { animation-delay: 2s; animation-duration: 15s; }
.p3 { animation-delay: 4s; animation-duration: 10s; }
.p4 { animation-delay: 1s; animation-duration: 14s; }
.p5 { animation-delay: 3s; animation-duration: 11s; }

.floating-chart {
   transform-origin: center;
   animation: floatChart 8s infinite ease-in-out;
}

.floating-cube {
   transform-origin: center;
   animation: rotateCube 12s infinite ease-in-out;
}
.c2 { animation-delay: 0s; }
.c3 { animation-delay: -6s; }

/* Brand Content overlay */
.brand-content {
   position: relative;
   z-index: 2;
   text-align: center;
   color: #ffffff;
   padding: 2rem;
}

.brand-title {
   font-size: 3.5rem;
   font-weight: 800;
   letter-spacing: 2px;
   margin-bottom: 0.5rem;
   background: linear-gradient(to right, #ffffff, #00d2ff);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   text-shadow: 0px 4px 15px rgba(0, 210, 255, 0.3);
}

.brand-subtitle {
   font-size: 1.25rem;
   font-weight: 400;
   color: #a0aec0;
   margin-bottom: 3rem;
}

.feature-list {
   display: flex;
   justify-content: center;
   gap: 2rem;
}

.feature-item {
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 0.75rem;
   opacity: 0.9;
   transition: transform 0.3s ease, opacity 0.3s ease;
}

.feature-item:hover {
   transform: translateY(-5px);
   opacity: 1;
}

.icon-box {
   width: 60px;
   height: 60px;
   border-radius: 16px;
   background: rgba(255, 255, 255, 0.05);
   backdrop-filter: blur(10px);
   border: 1px solid rgba(255, 255, 255, 0.1);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.75rem;
   color: #00d2ff;
   box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
}

.feature-item span {
   font-size: 0.95rem;
   font-weight: 500;
   letter-spacing: 0.5px;
}

/* =======================================
   Right Form Section (40%)
======================================= */
.form-section {
   flex: 0 0 40%;
   display: flex;
   align-items: center;
   justify-content: center;
   background: #16213e;
   padding: 2rem;
   position: relative;
}

/* subtle gradient behind form */
.form-section::before {
   content: '';
   position: absolute;
   top: -20%;
   right: -20%;
   width: 300px;
   height: 300px;
   border-radius: 50%;
   background: radial-gradient(circle, rgba(58,123,213,0.15) 0%, rgba(22,33,62,0) 70%);
   z-index: 0;
}

.glass-panel {
   width: 100%;
   max-width: 420px;
   padding: 3rem 2.5rem;
   background: rgba(255, 255, 255, 0.03);
   backdrop-filter: blur(16px);
   border: 1px solid rgba(255, 255, 255, 0.05);
   border-radius: 24px;
   box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
   position: relative;
   z-index: 1;
}

.form-header {
   margin-bottom: 2.5rem;
   text-align: left;
}

.form-header h2 {
   color: #ffffff;
   font-size: 2rem;
   font-weight: 700;
   margin-bottom: 0.5rem;
}

.form-header p {
   color: #718096;
   font-size: 0.95rem;
}

/* Error Alert */
.error-alert {
   background: rgba(229, 62, 62, 0.1);
   border-left: 4px solid #e53e3e;
   color: #fc8181;
   padding: 1rem;
   border-radius: 8px;
   margin-bottom: 1.5rem;
   font-size: 0.9rem;
   display: flex;
   align-items: center;
   gap: 0.5rem;
}

/* Form Inputs */
.login-form {
   display: flex;
   flex-direction: column;
   gap: 1.25rem;
}

.input-group {
   position: relative;
   width: 100%;
}

.input-icon {
   position: absolute;
   left: 1.25rem;
   top: 50%;
   transform: translateY(-50%);
   color: #718096;
   font-size: 1.2rem;
   transition: color 0.3s ease;
   z-index: 2;
}

.glass-input {
   width: 100%;
   padding: 1rem 1rem 1rem 3rem;
   background: rgba(255, 255, 255, 0.05);
   border: 1px solid rgba(255, 255, 255, 0.1);
   border-radius: 12px;
   color: #ffffff;
   font-size: 1rem;
   transition: all 0.3s ease;
}

.glass-input::placeholder {
   color: #4a5568;
}

.glass-input:focus {
   outline: none;
   background: rgba(255, 255, 255, 0.08);
   border-color: rgba(0, 210, 255, 0.5);
   box-shadow: 0 0 0 4px rgba(0, 210, 255, 0.1);
}

.glass-input:focus + .input-icon,
.glass-input:not(:placeholder-shown) + .input-icon {
   color: #00d2ff;
}

.glass-input:disabled {
   opacity: 0.5;
   cursor: not-allowed;
}

/* Form Actions */
.form-actions {
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-top: 0.5rem;
   margin-bottom: 0.5rem;
}

.checkbox-container {
   display: flex;
   align-items: center;
   position: relative;
   padding-left: 28px;
   cursor: pointer;
   font-size: 0.9rem;
   color: #a0aec0;
   user-select: none;
   transition: color 0.3s ease;
}

.checkbox-container:hover {
   color: #ffffff;
}

.checkbox-container input {
   position: absolute;
   opacity: 0;
   cursor: pointer;
   height: 0;
   width: 0;
}

.checkmark {
   position: absolute;
   top: 50%;
   left: 0;
   transform: translateY(-50%);
   height: 18px;
   width: 18px;
   background-color: rgba(255, 255, 255, 0.1);
   border: 1px solid rgba(255, 255, 255, 0.2);
   border-radius: 4px;
   transition: all 0.3s ease;
}

.checkbox-container:hover input ~ .checkmark {
   background-color: rgba(255, 255, 255, 0.15);
}

.checkbox-container input:checked ~ .checkmark {
   background-color: #3a7bd5;
   border-color: #3a7bd5;
}

.checkmark:after {
   content: "";
   position: absolute;
   display: none;
   left: 6px;
   top: 2px;
   width: 5px;
   height: 10px;
   border: solid white;
   border-width: 0 2px 2px 0;
   transform: rotate(45deg);
}

.checkbox-container input:checked ~ .checkmark:after {
   display: block;
}

.forgot-link {
   color: #a0aec0;
   font-size: 0.9rem;
   text-decoration: none;
   transition: color 0.3s ease;
}

.forgot-link:hover {
   color: #00d2ff;
}

/* Submit Button */
.submit-btn {
   width: 100%;
   padding: 1rem;
   border: none;
   border-radius: 12px;
   background: linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%);
   color: #ffffff;
   font-size: 1rem;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.3s ease;
   position: relative;
   overflow: hidden;
}

.submit-btn::before {
   content: '';
   position: absolute;
   top: 0;
   left: -100%;
   width: 100%;
   height: 100%;
   background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
   transition: left 0.5s ease;
}

.submit-btn:hover:not(:disabled) {
   transform: translateY(-2px);
   box-shadow: 0 10px 20px rgba(58, 123, 213, 0.4);
}

.submit-btn:hover:not(:disabled)::before {
   left: 100%;
}

.submit-btn:disabled {
   background: rgba(255, 255, 255, 0.1);
   color: rgba(255, 255, 255, 0.3);
   cursor: not-allowed;
   box-shadow: none;
}

.btn-content {
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 0.5rem;
}

.spin-icon {
   animation: spin 1s linear infinite;
}

@keyframes spin {
   100% { transform: rotate(360deg); }
}

/* Register Link */
.register-link {
   text-align: center;
   margin-top: 1rem;
   font-size: 0.95rem;
   color: #718096;
}

.register-link a {
   color: #00d2ff;
   text-decoration: none;
   font-weight: 500;
   margin-left: 0.25rem;
   transition: color 0.3s ease;
}

.register-link a:hover {
   color: #ffffff;
}

/* =======================================
   Responsive Design
======================================= */
@media (max-width: 991.98px) {
   .visual-section {
      display: none;
   }
   
   .form-section {
      flex: 0 0 100%;
   }
   
   .glass-panel {
      padding: 2.5rem 2rem;
   }
}

@media (max-width: 575.98px) {
   .glass-panel {
      padding: 2rem 1.5rem;
      border: none;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
   }
   
   .form-section {
      padding: 0;
   }
}

/* Modal Scroll Fixes (Keeping existing fixes) */
.no-scroll-modal {
   max-height: none !important;
   overflow: visible !important;
}

.modal-dialog {
   max-height: none !important;
}

.modal-content {
   max-height: none !important;
   overflow: visible !important;
}

.modal-body {
   max-height: none !important;
   overflow: visible !important;
}
</style>